import React, { useEffect, useState, useCallback } from 'react';
import { useGlobalSocket } from './WebSocketManager';

interface ChatNotification {
  id: string;
  sessionKey: string;
  guestName: string;
  message: string;
  timestamp: number;
  hotelId?: number;
}

interface ChatNotificationSystemProps {
  enableSound?: boolean;
  maxNotifications?: number;
  autoHideTime?: number;
}

export const ChatNotificationSystem: React.FC<ChatNotificationSystemProps> = ({
  enableSound = true,
  maxNotifications = 3,
  autoHideTime = 5000
}) => {
  const [notifications, setNotifications] = useState<ChatNotification[]>([]);
  const { isConnected } = useGlobalSocket();

  // Verificar se está na página de comunicação
  const isOnCommunicationPage = () => {
    const path = window.location.pathname;
    return path.includes('communication') || path.includes('CommunicationCenter');
  };

  // Reproduzir som de notificação
  const playNotificationSound = useCallback(() => {
    if (!enableSound) return;
    
    try {
      // Criar beep simples usando Web Audio API
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.warn('Erro ao reproduzir som de notificação:', error);
    }
  }, [enableSound]);

  // Escutar eventos de WebSocket
  useEffect(() => {
    const handleWebSocketMessage = (event: any) => {
      const { event: eventName, data } = event.detail;

      // Se está na página de comunicação, não mostrar notificações
      if (isOnCommunicationPage()) {
        return;
      }

      // Processar apenas eventos de mensagem
      if (!['guest-new-message', 'new-message', 'guest-message', 'bidirectional-message'].includes(eventName)) {
        return;
      }

      const sessionKey = data.sessionKey || data.sessionId || data.session_key;
      const guestName = data.guestName || data.sender || 'Hóspede';
      const message = data.message?.text || data.text || data.message || 'Nova mensagem';

      if (!sessionKey || !message) return;

      const notification: ChatNotification = {
        id: `${sessionKey}-${Date.now()}`,
        sessionKey,
        guestName,
        message: message.length > 100 ? message.substring(0, 100) + '...' : message,
        timestamp: Date.now(),
        hotelId: data.hotelId
      };

      console.log('📢 Nova notificação de chat:', notification);

      // Adicionar notificação
      setNotifications(prev => {
        const newNotifications = [notification, ...prev].slice(0, maxNotifications);
        return newNotifications;
      });

      // Reproduzir som
      playNotificationSound();

      // Mostrar notificação do navegador se permitido
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(`${guestName}`, {
          body: message,
          icon: '/favicon.ico',
          tag: sessionKey // Evitar duplicatas
        });
      }
    };

    window.addEventListener('websocket-message', handleWebSocketMessage);

    return () => {
      window.removeEventListener('websocket-message', handleWebSocketMessage);
    };
  }, [maxNotifications, playNotificationSound]);

  // Auto-ocultar notificações
  useEffect(() => {
    if (notifications.length === 0) return;

    const timer = setTimeout(() => {
      setNotifications(prev => prev.slice(0, -1));
    }, autoHideTime);

    return () => clearTimeout(timer);
  }, [notifications, autoHideTime]);

  // Solicitar permissão para notificações do navegador
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        console.log('Permissão para notificações:', permission);
      });
    }
  }, []);

  // Não renderizar se não estiver conectado ou se estiver na página de comunicação
  if (!isConnected || isOnCommunicationPage() || notifications.length === 0) {
    return null;
  }

  const handleNotificationClick = (sessionKey: string) => {
    // Navegar para a página de comunicação com a sessão específica
    window.open(`/communication-center?session=${sessionKey}`, '_blank');
    
    // Remover notificação clicada
    setNotifications(prev => prev.filter(n => n.sessionKey !== sessionKey));
  };

  const handleDismissAll = () => {
    setNotifications([]);
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 80,
        right: 20,
        zIndex: 9998,
        maxWidth: '400px',
        width: '100%'
      }}
    >
      {/* Header com contador */}
      {notifications.length > 0 && (
        <div
          style={{
            background: '#2563eb',
            color: 'white',
            padding: '8px 15px',
            borderRadius: '8px 8px 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          <span>💬 {notifications.length} nova(s) mensagem(s)</span>
          <button
            onClick={handleDismissAll}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '16px',
              padding: '0 5px'
            }}
            title="Dispensar todas"
          >
            ✕
          </button>
        </div>
      )}

      {/* Lista de notificações */}
      {notifications.map((notification, index) => (
        <div
          key={notification.id}
          onClick={() => handleNotificationClick(notification.sessionKey)}
          style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderTop: index === 0 && notifications.length > 1 ? 'none' : undefined,
            borderRadius: index === notifications.length - 1 ? '0 0 8px 8px' : '0',
            padding: '12px 15px',
            cursor: 'pointer',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease',
            animation: index === 0 ? 'slideInRight 0.3s ease-out' : undefined
          }}
          onMouseEnter={(e) => {
            (e.target as HTMLElement).style.background = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLElement).style.background = 'white';
          }}
        >
          <div 
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px'
            }}
          >
            {/* Avatar */}
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: '#10b981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '14px',
                fontWeight: 'bold',
                flexShrink: 0
              }}
            >
              {notification.guestName.charAt(0).toUpperCase()}
            </div>

            {/* Conteúdo */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div 
                style={{
                  fontWeight: 'bold',
                  fontSize: '14px',
                  color: '#1f2937',
                  marginBottom: '4px'
                }}
              >
                {notification.guestName}
              </div>
              <div 
                style={{
                  fontSize: '13px',
                  color: '#6b7280',
                  lineHeight: '1.4',
                  wordBreak: 'break-word'
                }}
              >
                {notification.message}
              </div>
              <div 
                style={{
                  fontSize: '11px',
                  color: '#9ca3af',
                  marginTop: '4px'
                }}
              >
                {new Date(notification.timestamp).toLocaleTimeString()}
              </div>
            </div>

            {/* Indicador */}
            <div
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#ef4444',
                flexShrink: 0,
                marginTop: '4px'
              }}
            />
          </div>
        </div>
      ))}

      <style>
        {`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ChatNotificationSystem;